/**
 * UX-C04 — ProactiveEngine.
 *
 * Mounted once inside the authenticated layout. Evaluates the rule registry
 * every 30s, exposes the active suggestions through a tiny store, persists
 * dismissals to localStorage and silences a rule for 24h after 3 dismissals.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { RULES, type ProactiveContext, type ProactiveRule, type UIAction } from './rules';

const STORAGE_KEY = 'expai_proactive_dismissals_v1';
const SILENCE_MS = 24 * 60 * 60 * 1000;
const SILENCE_AFTER = 3;
const TICK_MS = 30 * 1000;
const MAX_SUGGESTIONS = 3;

export interface ActiveSuggestion {
  ruleId: string;
  title: string;
  text: string;
  action: UIAction;
}

interface DismissalState {
  count: number;
  silencedUntil: number;
}

type DismissalMap = Record<string, DismissalState>;

interface ProactiveStore {
  suggestions: ActiveSuggestion[];
  setSuggestions: (s: ActiveSuggestion[]) => void;
  dismissals: DismissalMap;
  dismiss: (ruleId: string) => void;
}

function loadDismissals(): DismissalMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as DismissalMap;
  } catch {
    return {};
  }
}

function saveDismissals(map: DismissalMap) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

export const useProactiveStore = create<ProactiveStore>((set, get) => ({
  suggestions: [],
  setSuggestions: (s) => set({ suggestions: s }),
  dismissals: loadDismissals(),
  dismiss: (ruleId: string) => {
    const map = { ...get().dismissals };
    const prev = map[ruleId] ?? { count: 0, silencedUntil: 0 };
    const count = prev.count + 1;
    const silencedUntil = count >= SILENCE_AFTER ? Date.now() + SILENCE_MS : prev.silencedUntil;
    map[ruleId] = { count, silencedUntil };
    saveDismissals(map);
    set({
      dismissals: map,
      suggestions: get().suggestions.filter((s) => s.ruleId !== ruleId),
    });
  },
}));

/** Build the context the rules consume. Wire real data sources here as available. */
function buildContext(): ProactiveContext {
  // Lightweight mock context — replaceable with selectors from real stores.
  return {
    kpi: { oee: 68.4, line: 'L-01' },
    ncs: [
      { id: 'NC-1042', status: 'open', openedAt: Date.now() - 3 * 60 * 60 * 1000 },
    ],
    cameras: [{ id: 'CAM-07', driftPct: 6.2 }],
    now: Date.now(),
  };
}

function evaluate(rules: ProactiveRule[], dismissals: DismissalMap): ActiveSuggestion[] {
  const ctx = buildContext();
  const now = Date.now();
  const out: ActiveSuggestion[] = [];
  for (const r of rules) {
    const d = dismissals[r.id];
    if (d && d.silencedUntil > now) continue;
    try {
      if (!r.condition(ctx)) continue;
      out.push({ ruleId: r.id, title: r.title, text: r.suggestion(ctx), action: r.action(ctx) });
    } catch {
      // skip broken rule silently
    }
    if (out.length >= MAX_SUGGESTIONS) break;
  }
  return out;
}

export function ProactiveEngine() {
  const setSuggestions = useProactiveStore((s) => s.setSuggestions);
  const dismissals = useProactiveStore((s) => s.dismissals);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setSuggestions(evaluate(RULES, useProactiveStore.getState().dismissals));
    };
    tick();
    const id = window.setInterval(tick, TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [setSuggestions, dismissals]);

  return null;
}
